import { useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import isEqual from "lodash/isEqual";
import {
  createColumnHelper,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import { remoteData } from "../../api/common";
import TableFooter from "../Table/TableFooter";
import {
  AttributeauthorityApiGetOrganizationsRequest,
  OrganizationWithReferentsAndStatus
} from "../../api/generated_backoffice";
import Pager from "../Table/Pager";
import TableHeader from "../Table/TableHeader";
import TableBody from "../Table/TableBody";
import { getEntityTypeLabel } from "../../utils/strings";
import { organizationStatusBadge } from "../../utils/badges";
import { BadgePill } from "../BadgePill";
import { useDebouncedValue } from "../../utils/useDebounce";
import {
  usePaginationHelpers,
  withExpandedReset
} from "../../utils/usePaginationHelpers";
import { useSyncSorting } from "../../utils/useSyncSorting";
import { ExpanderCell } from "../ExpanderCell/ExpanderCell";
import ActivationsFilter from "./ActivationsFilter";
import OperatorActivationDetail from "./OperatorActivationDetail";

type OrderType = "fiscalCode" | "name" | "pec" | "insertedAt";

export type ActivationsFilterFormValues = {
  searchQuery: string | undefined;
  sortColumn: OrderType | undefined;
  sortDirection: "ASC" | "DESC" | undefined;
};

const activationsFilterFormInitialValues: ActivationsFilterFormValues = {
  searchQuery: undefined,
  sortColumn: undefined,
  sortDirection: undefined
};

const getActivationsSortColumn = (id: string): OrderType => {
  switch (id) {
    case "organizationFiscalCode":
      return "fiscalCode";
    case "insertedAt":
      return "insertedAt";
    case "organizationName":
    default:
      return "name";
  }
};

const OperatorActivations = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20
  });
  const [values, setValues] = useState<ActivationsFilterFormValues>(
    activationsFilterFormInitialValues
  );

  const hasActiveFitlers = !isEqual(values, activationsFilterFormInitialValues);

  const searchQueryDebounced = useDebouncedValue({
    value: values.searchQuery,
    delay: 500,
    leading: false,
    trailing: true,
    maxWait: 3000
  });

  const params = useMemo(
    (): AttributeauthorityApiGetOrganizationsRequest => ({
      searchQuery: searchQueryDebounced,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortBy: values.sortColumn,
      sortDirection: values.sortDirection
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      searchQueryDebounced,
      values.sortColumn,
      values.sortDirection
    ]
  );

  const {
    data: operators,
    isPending,
    refetch
  } = remoteData.Backoffice.AttributeAuthority.getOrganizations.useQuery(
    params
  );

  const columnHelper = createColumnHelper<OrganizationWithReferentsAndStatus>();

  const columns = [
    columnHelper.accessor("organizationName", {
      header: "RAGIONE SOCIALE",
      size: 350
    }),
    columnHelper.display({
      id: "entityType",
      header: "TIPOLOGIA ENTE",
      enableSorting: false,
      size: 150,
      cell: ({ row }) => getEntityTypeLabel(row.original.entityType)
    }),
    columnHelper.accessor("referents", {
      header: "UTENTI ABILITATI",
      enableSorting: false,
      size: 200,
      cell: ({ getValue }) => {
        const list = getValue() ?? [];
        if (list.length === 0) {
          return <span>-</span>;
        }
        const shown = list.slice(0, 2);
        const extra = list.length - shown.length;
        return (
          <span>
            {shown.join(", ")}
            {extra > 0 ? ` +${extra}` : ""}
          </span>
        );
      }
    }),
    columnHelper.accessor("insertedAt", {
      header: "AGGIUNTO IL",
      size: 150,
      cell: ({ getValue }) => {
        const v = getValue();
        return <span>{v ? format(new Date(v), "dd/MM/yyyy") : "-"}</span>;
      }
    }),
    columnHelper.accessor("status", {
      header: "STATO",
      size: 150,
      enableSorting: false,
      cell: ({ getValue }) => (
        <BadgePill {...organizationStatusBadge[getValue()]} />
      )
    }),

    columnHelper.display({
      id: "expander",
      header: () => null,
      enableSorting: false,
      size: 100,
      cell: ({ row }) => <ExpanderCell row={row} />
    })
  ];

  const data: Array<OrganizationWithReferentsAndStatus> = useMemo(
    () => (operators?.items ? [...operators.items] : []),
    [operators]
  );

  const pageCount = Math.ceil((operators?.count ?? 0) / pagination.pageSize);

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      sorting,
      expanded
    },
    onPaginationChange: withExpandedReset(setExpanded, setPagination),
    onSortingChange: withExpandedReset(setExpanded, setSorting),
    onExpandedChange: setExpanded,
    manualPagination: true,
    manualSorting: true,
    sortDescFirst: false,
    pageCount,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const handleFilterChange = useCallback(
    (
      update:
        | ActivationsFilterFormValues
        | ((prev: ActivationsFilterFormValues) => ActivationsFilterFormValues)
    ) => {
      setValues(update);
      setExpanded({});
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    },
    []
  );

  useSyncSorting(sorting, setValues, getActivationsSortColumn);
  const { canPreviousPage, canNextPage, previousPage, nextPage, gotoPage } =
    usePaginationHelpers(table);

  const startRowIndex: number = pagination.pageIndex * pagination.pageSize + 1;
  // eslint-disable-next-line functional/no-let
  let endRowIndex: number = startRowIndex - 1 + pagination.pageSize;

  if (operators?.count && endRowIndex > operators?.count) {
    endRowIndex = operators.count;
  }

  const pageArray = Array.from(Array(pageCount).keys());

  return (
    <section className="px-8 py-10 bg-white">
      <ActivationsFilter
        values={values}
        onChange={handleFilterChange}
        hasActiveFitlers={hasActiveFitlers}
        onReset={() => handleFilterChange(activationsFilterFormInitialValues)}
      />
      <Pager
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        startRowIndex={startRowIndex}
        endRowIndex={endRowIndex}
        pageIndex={pagination.pageIndex}
        onPreviousPage={previousPage}
        onNextPage={nextPage}
        onGotoPage={gotoPage}
        pageArray={pageArray}
        total={operators?.count}
        isPending={isPending}
      />
      <div className="overflow-auto">
        <table className="w-100 bg-white">
          <TableHeader headerGroups={table.getHeaderGroups()} />

          <TableBody
            table={table}
            renderExpanded={row => (
              <OperatorActivationDetail
                operator={row.original}
                getActivations={() => refetch()}
              />
            )}
          />
        </table>
      </div>
      <TableFooter
        isPending={isPending}
        isEmpty={!operators?.items?.length}
        hasActiveFilters={hasActiveFitlers}
        emptyMessage="Nessun operatore trovato"
        onReset={() => handleFilterChange(activationsFilterFormInitialValues)}
      />
    </section>
  );
};

export default OperatorActivations;
